import { type OperationVariables } from '@apollo/react-common';
import { type GraphQLTaggedNode } from 'react-relay';
import { createOperationDescriptor, getRequest } from 'relay-runtime';

import { type QueryInteropFetchPolicy } from '../mapFetchPolicyForQuery.ts';
import { retainOperationForSSR } from '../retainForSSR.ts';
import { toDocumentNode } from '../toDocumentNode.ts';

import {
	type CacheBridgeCache,
	type CacheWriteResult as CommonCacheWriteResult,
	getFailureContext,
	getSecondaryCache,
	skipped,
	writeSafely,
	written,
} from './cacheBridgeCommon.ts';
import { getApolloRelayCacheBridgeDisabledReason } from './cacheBridgeGate.ts';
import { addRelayConcreteTypenamesToQueryData } from './queryResultTypenames.ts';
import { getApolloClient } from './utils/getApolloClient.ts';
import { getRelayEnvironment } from './utils/getRelayEnvironment.ts';
import { shouldSkipCacheBridgeSync } from './utils/shouldSkipCacheBridgeSync.ts';

export type CacheWriteResult = CommonCacheWriteResult;

export type QueryCacheBridgeSource = CacheBridgeCache;

export type QueryCacheBridgeWriteArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = {
	query: GraphQLTaggedNode;
	variables?: TVariables;
	data?: TData | null;
	fetchPolicy?: QueryInteropFetchPolicy;
	useAggClient?: boolean;
};

export type QueryCacheBridgeSyncArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = QueryCacheBridgeWriteArgs<TData, TVariables> & {
	source: QueryCacheBridgeSource;
	error?: unknown;
	skip?: boolean;
};

function writeQueryToApollo<TData, TVariables extends Record<string, any> = OperationVariables>(
	args: QueryCacheBridgeWriteArgs<TData, TVariables>,
): CacheWriteResult {
	const skipReason = shouldSkipCacheBridgeSync(args);
	if (skipReason) return skipped(skipReason);

	const client = getApolloClient({ useAggClient: args.useAggClient });
	if (!client) return skipped('apollo-client-missing');

	return writeSafely(
		() => {
			const data = addRelayConcreteTypenamesToQueryData(args.query, args.data as TData);

			// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- cache bridge intentionally mirrors Relay query results into Apollo during migration
			client.writeQuery({
				query: toDocumentNode(args.query),
				variables: args.variables,
				data,
			});
			return written();
		},
		getFailureContext('query', 'write', 'apollo'),
	);
}

function writeQueryToRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	args: QueryCacheBridgeWriteArgs<TData, TVariables>,
): CacheWriteResult {
	const skipReason = shouldSkipCacheBridgeSync(args);
	if (skipReason) return skipped(skipReason);

	const environment = getRelayEnvironment();
	if (!environment) return skipped('relay-environment-missing');

	return writeSafely(
		() => {
			const operation = createOperationDescriptor(getRequest(args.query), args.variables ?? {});
			environment.commitPayload(operation, args.data as Record<string, unknown>);
			// commitPayload writes without retaining. During SSR no component effect
			// retains the operation before the synchronous render reads it, so retain
			// it here to keep the mirrored data available for the SSR read.
			retainOperationForSSR(environment, args.query, args.variables ?? {});
			return written();
		},
		getFailureContext('query', 'write', 'relay'),
	);
}

function writeQueryToCache<TData, TVariables extends Record<string, any> = OperationVariables>(
	args: QueryCacheBridgeWriteArgs<TData, TVariables>,
	targetCache: CacheBridgeCache,
): CacheWriteResult {
	return targetCache === 'apollo' ? writeQueryToApollo(args) : writeQueryToRelay(args);
}

export function syncQueryCacheBridge<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>({
	source,
	error,
	skip,
	...writeArgs
}: QueryCacheBridgeSyncArgs<TData, TVariables>): CacheWriteResult {
	// Query mirroring is secondary work: only successful, non-skipped primary
	// operations should attempt to populate the opposite cache.
	if (skip) return skipped('operation-skipped');
	if (error) return skipped('operation-error');

	const disabledReason = getApolloRelayCacheBridgeDisabledReason();
	if (disabledReason) return skipped(disabledReason);

	return writeQueryToCache(writeArgs, getSecondaryCache(source));
}
